'use client';

import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Input } from '../ui/input';
import {
  Search,
  Bell,
  Settings,
  User,
  LogOut,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  Check,
  XCircle,
  Clock,
  Calendar,
  DollarSign,
  FileText,
  Receipt,
  Package,
  Fullscreen,
  ExternalLinkIcon,
  CalendarDays,
  Building2,
  LayoutDashboard,
  Eye,
  Edit,
  UserSearch,
} from 'lucide-react';
import { Button } from "../ui/button";
import { SidebarTrigger } from '../ui/sidebar';
import { AuthContext } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { getNotificationRoute } from "../../utils/notificationRoutes";
import { cn } from "../../utils/tailwindUtils";
import { Avatar } from "antd";
import male from "../../assets/svg/Male.svg";
import { useNavigate, useLocation } from "react-router-dom";
import useApiMethods from "../../lib/useApiMethods";
import { API_URL, LOGOUT_URL, BRANCH, EMPLOYEE_PERSONAL_INFO } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { isBranchAvail, clearActiveBranch, getActiveBranchName, getActiveBranchId, getActiveBranchIsMain, isOrgAdminUser, shouldShowOrgAdminBranchUI, BRANCH_FILTER_CHANGED_EVENT, setActiveBranch, setOverviewMode } from "../../utils/branchSession";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../../hooks/use-navigation";


/** Join the API base and a path without producing a double slash.
 *  API_URL is edited per environment and sometimes carries a trailing slash,
 *  while every path constant starts with one. axios normalises that for the
 *  rest of the app; raw fetch does not, and the doubled slash does not match
 *  any Django route -- it returns the HTML 404 page, which then fails to
 *  parse as JSON. */
const apiUrl = (path) => `${API_URL.replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;

export function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const {
    unreadCount,
    notifications,
    refreshList,
    markRead,
    markAllRead,
  } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        refreshList();
      }
      return next;
    });
  };

  const getNotificationIcon = (notification) => {
    const { notification_type, notification_action } = notification;

    // Leave notifications
    if (notification_type === 'Leave') {
      switch (notification_action) {
        case 'approved':
          return <CheckCircle className="h-4 w-4 text-green-600" />;
        case 'rejected':
          return <XCircle className="h-4 w-4 text-red-600" />;
        case 'request':
          return <FileText className="h-4 w-4 text-blue-600" />;
        default:
          return <Calendar className="h-4 w-4 text-blue-600" />;
      }
    }

    // Attendance notifications
    if (notification_type === 'Attendance') {
      return <Clock className="h-4 w-4 text-orange-600" />;
    }

    if (notification_type === 'AttendanceException') {
      return <AlertTriangle className="h-4 w-4 text-rose-600" />;
    }

    if (notification_type === 'AttendanceExceptionManager') {
      return <Clock className="h-4 w-4 text-amber-600" />;
    }

    if (notification_type === 'AttendanceDiscrepancy') {
      return <AlertTriangle className="h-4 w-4 text-orange-600" />;
    }

    // Payroll notifications
    if (notification_type === 'Payroll') {
      return <DollarSign className="h-4 w-4 text-green-600" />;
    }

    // Reimbursement notifications
    if (notification_type === 'Reimbursement') {
      switch (notification_action) {
        case 'approved':
          return <CheckCircle className="h-4 w-4 text-green-600" />;
        case 'rejected':
          return <XCircle className="h-4 w-4 text-red-600" />;
        default:
          return <Receipt className="h-4 w-4 text-purple-600" />;
      }
    }

    // Asset notifications
    if (notification_type === 'Asset') {
      return <Package className="h-4 w-4 text-indigo-600" />;
    }

    if (notification_type === 'OccupancyShortage') {
      return <Building2 className="h-4 w-4 text-rose-600" />;
    }

    if (notification_type === 'Shift Change' || notification_type === 'Shift Assignment Pending') {
      return <CalendarDays className="h-4 w-4 text-blue-600" />;
    }

    // General/System notifications
    if (notification_type === 'System') {
      return <Settings className="h-4 w-4 text-gray-600" />;
    }

    // Default icon
    return <Bell className="h-4 w-4 text-gray-600" />;
  };

  // Format time function for relative time
  const formatRelativeTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_opened) {
      await markRead(notification.id);
    }
    setOpen(false);

    const route = getNotificationRoute(notification);
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        onClick={handleToggleOpen}
        variant="ghost"
        size="icon"
        className={cn(
          "relative rounded-full h-9 w-9",
          "text-muted-foreground hover:text-foreground",
          "bg-transparent hover:bg-accent",
          "transition-colors duration-200"
        )}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {open && (
        <div className={cn(
          "absolute right-0 mt-2 w-[min(24rem,calc(100vw-2rem))] rounded-lg border bg-popover text-popover-foreground",
          "shadow-lg z-50 overflow-hidden",
          "animate-in fade-in-80 slide-in-from-top-2",
          "max-h-[min(24rem,calc(100vh-5rem))]"
        )}>
          {/* Header */}
          <div className={cn(
            "flex items-center justify-between px-4 py-3",
            "border-b bg-muted/50"
          )}>
            <h3 className="text-sm font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                className={cn(
                  "text-xs font-medium text-primary",
                  "hover:underline transition-colors"
                )}>
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-64 overflow-y-auto">
            {notifications.length > 0 ? (
              <ul className="divide-y divide-border">
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={cn(
                      "px-4 py-3 cursor-pointer transition-colors",
                      "hover:bg-accent/50",
                      !notification.is_opened && "bg-accent/30"
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      {/* Notification Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification)}
                      </div>

                      {/* Notification Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <p className={cn(
                            "text-sm font-medium",
                            !notification.is_opened ? "text-foreground" : "text-muted-foreground"
                          )}>
                            {notification.title}
                          </p>
                          <div className={cn(
                            "flex-shrink-0 h-2 w-2 rounded-full ml-2 mt-1",
                            !notification.is_opened ? "bg-primary" : "bg-transparent"
                          )} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(notification.time)}
                          </span>
                          <span className={cn(
                            "text-xs px-2 py-1 rounded-full capitalize",
                            "bg-muted text-muted-foreground"
                          )}>
                            {notification.notification_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-8 text-center">
                <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Full-screen toggle hook — defined at module scope (not inside a component).
function useFullScreen() {
  const [isFullScreen, setIsFullScreen] = useState(false);

  const enterFullScreen = async () => {
    const docElement = document.documentElement;
    try {
      if (docElement.requestFullscreen) {
        await docElement.requestFullscreen();
      } else if (docElement.mozRequestFullScreen) {
        await docElement.mozRequestFullScreen();
      } else if (docElement.webkitRequestFullscreen) {
        await docElement.webkitRequestFullscreen();
      } else if (docElement.msRequestFullscreen) {
        await docElement.msRequestFullscreen();
      }
    } catch (error) {
      console.error('Error entering full screen:', error);
    }
  };

  const exitFullScreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (error) {
      console.error('Error exiting full screen:', error);
    }
  };

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await enterFullScreen();
      } else {
        await exitFullScreen();
      }
    } catch (error) {
      console.error('Full screen error:', error);
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
    events.forEach(event => document.addEventListener(event, handleFullScreenChange));
    return () => events.forEach(event => document.removeEventListener(event, handleFullScreenChange));
  }, []);

  return { isFullScreen, toggleFullScreen };
}

// Shared dropdown shell — used by user menu and branch switcher.
const headerDropdownPanelClass = cn(
  "absolute right-0 top-12 w-72 rounded-xl border shadow-lg z-50 overflow-hidden backdrop-blur-sm",
  "animate-in fade-in-80 slide-in-from-top-2 font-sans",
  "bg-popover text-popover-foreground border-border",
);

const headerDropdownHeaderClass = cn(
  "px-5 py-4 border-b",
  "bg-sidebar/10 dark:bg-sidebar/20",
  "border-border",
);

const headerDropdownItemClass = (selected = false) => cn(
  "w-full flex items-start gap-3 px-5 py-3 text-sm group transition-all duration-150 ease-in-out border-l-2 font-sans text-left",
  selected
    ? "border-l-sidebar-primary bg-sidebar/10 text-foreground dark:bg-sidebar/20"
    : "border-l-transparent hover:bg-accent hover:text-accent-foreground hover:border-l-sidebar-primary/60",
);

const headerDropdownIconClass = (selected = false) => cn(
  "flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-md",
  selected
    ? "bg-sidebar-primary/15 text-sidebar-primary"
    : "bg-muted text-muted-foreground group-hover:bg-sidebar/10 group-hover:text-sidebar-primary",
);

export function UserDropdown() {
  const { logoutauth } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const employee_name = sessionStorage.getItem('employee_name')
  const profile = sessionStorage.getItem('profile')
  const department = sessionStorage.getItem('department')
  const designation = sessionStorage.getItem('designation')
  const companyName = sessionStorage.getItem('companyName')

  const navigate = useNavigate();

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { isFullScreen, toggleFullScreen } = useFullScreen();

  const menuItems = [
    {
      icon: User,
      label: "My Profile",
      onClick: () => { setOpen(false); navigate("/employeeprofile"); },
      description: "View your profile details"
    },
    // Switch Branch — org admin with branches only. Clears the active branch
    // and returns to the branch-selection gate to pick another.
    ...(shouldShowOrgAdminBranchUI() ? [{
      icon: Building2,
      label: "Switch Branch",
      onClick: () => { setOpen(false); clearActiveBranch(); navigate("/branches"); },
      description: getActiveBranchName() ? `Current: ${getActiveBranchName()}` : "Change the active branch"
    }] : []),
    {
      icon: isFullScreen ? ExternalLinkIcon : Fullscreen, // Fixed: use ternary operator
      label: isFullScreen ? "Exit Full Screen" : "Full Screen",
      onClick: toggleFullScreen,
      description: isFullScreen ? "Exit full screen mode" : "Change full screen mode"
    },
    {
      icon: Settings,
      label: "Settings",
      onClick: () => navigate("/settings"),
      description: "Customize your preferences"
    },
  ];

  // your logout handler
  const handleLogout = async () => {
    try {
      const authtoken = sessionStorage.getItem("token");

      const requestOptions = {
        method: 'GET',
        headers: { Authorization: `token ${authtoken}` },
      };

      const res = await fetch(apiUrl(LOGOUT_URL), requestOptions);
      const data = await res.json();

      if (data?.status === 'success') {
        logoutauth();
        navigate('/');
      } else {
        ToastMsg('error', data?.message || 'Logout failed');
      }
    } catch (error) {
      ToastMsg('error', error.message);
    }
  };

  return (
    <div className="relative font-sans" ref={menuRef}>
      {/* User Button */}
      <Button
        onClick={() => setOpen(!open)}
        variant="ghost"
        className={cn(
          "h-11 px-2 gap-2",
          open ? "bg-accent" :
            "bg-transparent hover:bg-accent",
          "text-foreground hover:text-foreground",
          "transition-colors duration-200",
          "border border-transparent hover:border-border"
        )}
      >
        <div className="h-7 w-7 rounded-full bg-sidebar-primary flex items-center justify-center text-white text-xs font-semibold overflow-hidden">
          <Avatar
            src={profile || male}
            className="w-full h-full object-cover"
          />
        </div>
        {/* <div className="hidden sm:flex flex-col items-start">
          <span className="text-sm font-medium">{employee_name}</span>
          <span className="text-xs text-muted-foreground">{department}{" "}|{" "}{designation}</span>
        </div> */}
        <ChevronDown className={cn(
          "h-4 w-4 text-muted-foreground transition-transform",
          open && "rotate-180"
        )} />
      </Button>

      {/* Dropdown Menu */}
      {open && (
        <div className={headerDropdownPanelClass}>
          {/* User Info Header */}
          <div
            role="button"
            onClick={() => { setOpen(false); navigate("/employeeprofile"); }}
            className={cn(headerDropdownHeaderClass, "cursor-pointer hover:bg-sidebar/15 transition")}
            title="View my profile"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-sidebar-primary flex items-center justify-center overflow-hidden">
                <Avatar
                  src={profile || male}
                  size="large"
                  className="cursor-pointer scale-125 hover:shadow-lg"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{employee_name}</p>
                <p className="text-xs text-muted-foreground truncate">{department}{" "}|{" "}{designation}</p>
              </div>
            </div>
            <p className="text-xs text-sidebar-primary font-medium truncate">
              {companyName}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.onClick}
                className={headerDropdownItemClass()}
              >
                <div className={headerDropdownIconClass()}>
                  <item.icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{item.label}</p>
                  <p className="text-xs text-muted-foreground group-hover:text-foreground">
                    {item.description}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Logout Section */}
          <div className="border-t border-border">
            <button
              onClick={handleLogout}
              className={cn(
                "w-full flex items-center gap-3 px-5 py-3 text-sm group transition-all duration-150 ease-in-out border-l-2 border-l-transparent",

                // Light mode
                "text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-l-red-500",

                // Dark mode
                "dark:text-red-400 dark:hover:bg-red-900/40 dark:hover:text-red-300 dark:hover:border-l-red-500"
              )}
            >
              <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-md 
          bg-red-100 text-red-600 group-hover:text-red-700
          dark:bg-red-900/30 dark:text-red-400 dark:group-hover:text-red-300"
              >
                <LogOut className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">Log out</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Branch control shown in the header for an org admin.

function BranchSwitcherOption({ opt, selected, variant = "branch", onSelect }) {
  const isAll = variant === "overview";
  const Icon = isAll ? LayoutDashboard : Building2;
  const description = isAll
    ? "Compare all locations"
    : (opt.is_main_branch ? "Main branch workspace" : "Branch workspace");

  return (
    <button
      type="button"
      onClick={() => onSelect(opt)}
      title={opt.name}
      className={headerDropdownItemClass(selected)}
    >
      <div className={headerDropdownIconClass(selected)}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn("font-medium leading-snug", selected && "text-sidebar-primary")}>
          {opt.name}
          {!isAll && opt.is_main_branch && (
            <span className="ml-1.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-sidebar-primary">
              Main
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 truncate">
          {description}
        </p>
      </div>
      {selected && <Check className="h-4 w-4 shrink-0 text-sidebar-primary mt-0.5" />}
    </button>
  );
}

function BranchHeaderControl() {
  const { fetch } = useApiMethods();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = getActiveBranchId(); // "all" | "<id>" | null

  useEffect(() => {
    fetch(BRANCH, { drop_down: "True" })
      .then((res) => {
        const raw = res?.data;
        const list = Array.isArray(raw) ? raw : (raw?.result || []);
        setBranches(Array.isArray(list) ? list : []);
      })
      .catch(() => setBranches([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const currentKey = current || "all";
  const currentBranch = branches.find((b) => String(b.id) === String(currentKey));
  const isOverview = currentKey === "all";
  const currentLabel = isOverview
    ? "All Branches"
    : (currentBranch?.name || getActiveBranchName() || "Select branch");
  const currentIsMain = !isOverview && (!!currentBranch?.is_main_branch || getActiveBranchIsMain());
  const currentHint = isOverview ? "Organisation overview" : "Branch workspace";

  const select = (opt) => {
    const val = String(opt.id);
    if (val === "all") {
      setOverviewMode("all");
    } else {
      setActiveBranch(opt.id, opt.name, !!opt.is_main_branch);
    }
    navigate("/home");
    setOpen(false);
  };

  return (
    <div className="relative font-sans" ref={ref}>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        title={currentLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          "h-11 px-3 gap-2.5 min-w-[200px] max-w-[240px] justify-start",
          open ? "bg-accent" : "bg-transparent hover:bg-accent",
          "text-foreground hover:text-foreground",
          "border border-transparent hover:border-border transition-colors duration-200",
        )}
      >
        <div className="h-7 w-7 shrink-0 rounded-full bg-sidebar-primary flex items-center justify-center text-white">
          {isOverview ? <LayoutDashboard className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
        </div>
        <span className="min-w-0 flex-1 text-left leading-tight hidden sm:block">
          <span className="flex items-center gap-1 min-w-0">
            <span className="truncate text-sm font-medium">{currentLabel}</span>
            {currentIsMain && (
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-sidebar-primary">
                Main
              </span>
            )}
          </span>
          <span className="block truncate text-xs text-muted-foreground">{currentHint}</span>
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
          open && "rotate-180",
        )} />
      </Button>

      {open && (
        <div className={headerDropdownPanelClass}>
          <div className={headerDropdownHeaderClass}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-sidebar-primary flex items-center justify-center shrink-0">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">Switch context</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  Overview or a specific branch
                </p>
              </div>
            </div>
          </div>

          <div className="py-1 max-h-[min(360px,70vh)] overflow-y-auto">
            <BranchSwitcherOption
              opt={{ id: "all", name: "All Branches", is_main_branch: false }}
              selected={currentKey === "all"}
              variant="overview"
              onSelect={select}
            />

            {branches.length > 0 && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-700" />
                {branches.map((opt) => (
                  <BranchSwitcherOption
                    key={opt.id}
                    opt={opt}
                    selected={String(opt.id) === String(currentKey)}
                    onSelect={select}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const formatEmployeeSearchName = (emp) => {
  const parts = [emp?.title, emp?.first_name, emp?.last_name].filter(Boolean);
  return parts.join(" ").trim() || emp?.employee_name || "Employee";
};

const employeeDesignation = (emp) =>
  emp?.designation_id?.[0]?.designation_name
  || emp?.designation_id?.designation_name
  || "";

const employeeDepartment = (emp) =>
  emp?.department_id?.[0]?.department_name
  || emp?.department_id?.department_name
  || "";

const HEADER_SEARCH_WIDTH = "w-80 lg:w-96";
const HEADER_SEARCH_COLLAPSED = "w-9";
const HEADER_SEARCH_TRANSITION = "transition-[width] duration-300 ease-in-out";

const headerSearchBarClass = (expanded, alignEnd = false) =>
  cn(
    "flex items-center h-9 w-full rounded-lg overflow-hidden transition-all duration-200",
    expanded &&
      "bg-muted/50 border shadow-sm border-[#0BB5AC] ring-1 ring-[#0BB5AC]/25",
    !expanded && alignEnd && "justify-end"
  );

const headerSearchIconClass = (expanded, side = "left") =>
  cn(
    "flex-shrink-0 h-9 w-9 flex items-center justify-center transition-colors focus-visible:outline-none",
    expanded
      ? cn(
          "text-[#0BB5AC] hover:bg-[#0BB5AC]/10",
          side === "left" ? "border-r border-border" : "border-l border-border"
        )
      : "text-muted-foreground hover:text-foreground hover:bg-accent/50 bg-muted/50 border border-border rounded-lg shadow-sm"
  );

function EmployeeSearchBar({ expanded, onActivate }) {
  const navigate = useNavigate();
  const { fetch } = useApiMethods();
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const clearEmployeeSearch = () => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
  };

  useEffect(() => {
    if (!expanded) {
      clearEmployeeSearch();
      return;
    }
    const timer = window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [expanded]);

  useEffect(() => {
    const term = query.trim();
    if (!expanded || term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetchRef.current(EMPLOYEE_PERSONAL_INFO, {
          search: term,
          page_no: 1,
          page_size: 6,
        });
        if (cancelled) return;
        const list = response?.data?.result;
        setResults(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, expanded]);

  const handleView = (employee) => {
    navigate("/employee/viewDetails", {
      state: {
        row: employee,
        employeeID: employee.id,
        isActive: employee.is_active !== false,
      },
    });
    clearEmployeeSearch();
  };

  const handleEdit = (event, employee) => {
    event.stopPropagation();
    navigate("/employee/add", {
      state: {
        employeeId: employee.id,
        isEdit: true,
      },
    });
    clearEmployeeSearch();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      clearEmployeeSearch();
    }
  };

  const activateEmployeeSearch = (event) => {
    event.stopPropagation();
    onActivate?.();
    setIsOpen(true);
    window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 50);
  };

  const showDropdown = expanded && isOpen && query.trim().length >= 2;
  const hasResults = results.length > 0;

  return (
    <div
      className={cn(
        "relative hidden md:flex items-center shrink-0 employee-search-container",
        HEADER_SEARCH_TRANSITION,
        expanded ? HEADER_SEARCH_WIDTH : HEADER_SEARCH_COLLAPSED
      )}
    >
      <div className={headerSearchBarClass(expanded, !expanded)}>
        {expanded && (
          <Input
            ref={inputRef}
            type="search"
            autoComplete="off"
            placeholder="Search employees..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              onActivate?.();
              setIsOpen(true);
            }}
            className="h-9 flex-1 min-w-0 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 pl-3 pr-1"
          />
        )}

        <button
          type="button"
          onClick={activateEmployeeSearch}
          className={headerSearchIconClass(expanded, "right")}
          title="Search employees"
          aria-label="Search employees"
          aria-expanded={expanded}
        >
          <UserSearch className="h-4 w-4" />
        </button>
      </div>

      {showDropdown && (loading || hasResults) && (
        <div className={cn("absolute top-full right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto", HEADER_SEARCH_WIDTH)}>
          <div className="p-2">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1">
              Employees
            </div>
            {loading && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Searching employees...
              </div>
            )}
            {results.map((employee) => (
              <div
                key={employee.id}
                className={cn(
                  "w-full px-3 py-2 rounded-md hover:bg-accent",
                  "transition-colors duration-150 flex items-center gap-2"
                )}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sidebar-primary/10 text-sidebar-primary flex items-center justify-center text-xs font-semibold">
                  {(employee.first_name?.[0] || "E").toUpperCase()}
                </div>
                <button
                  type="button"
                  onClick={() => handleView(employee)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-sm font-medium truncate">
                    {formatEmployeeSearchName(employee)}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[employeeDesignation(employee), employeeDepartment(employee)].filter(Boolean).join(" · ") || employee.code || "Employee"}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                    title="View employee"
                    onClick={() => handleView(employee)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                    title="Edit employee"
                    onClick={(event) => handleEdit(event, employee)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showDropdown && !loading && !hasResults && (
        <div className={cn("absolute top-full right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50", HEADER_SEARCH_WIDTH)}>
          <div className="p-4 text-center text-sm text-muted-foreground">
            No employees found for "{query}"
          </div>
        </div>
      )}
    </div>
  );
}

export function SiteHeader({ menuItems }) {
  const mainNav = menuItems.filter(item => !item.isSecondary);
  const secondaryNav = menuItems.filter(item => item.isSecondary);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState('quick'); // 'quick' | 'employee' — always one open
  const [, setHeaderRevision] = useState(0);
  const quickNavigateInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isQuickExpanded = activeSearch === 'quick';
  const isEmployeeExpanded = activeSearch === 'employee';

  const activateQuickNavigate = () => {
    setActiveSearch('quick');
  };

  const clearQuickNavigateQuery = () => {
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  useEffect(() => {
    if (!isQuickExpanded) {
      setSearchQuery('');
      setIsSearchOpen(false);
      return;
    }
    const timer = window.setTimeout(() => {
      quickNavigateInputRef.current?.focus({ preventScroll: true });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [isQuickExpanded]);

  useEffect(() => {
    const refresh = () => setHeaderRevision((n) => n + 1);
    window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, refresh);
    window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, refresh);
      window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, refresh);
    };
  }, []);

  const inOverviewMode = sessionStorage.getItem("active_branch_mode") === "overview";
  const showHeaderBranchControl =
    shouldShowOrgAdminBranchUI() &&
    !(inOverviewMode && location.pathname.startsWith("/settings"));

  // Flatten all menu items including subItems for search
  const allMenuItems = useMemo(() => {
    const items = [];

    const flattenItems = (itemList, parentLabel = '') => {
      itemList.forEach(item => {
        // Add current item
        items.push({
          ...item,
          parentLabel: parentLabel,
          fullPath: item.href,
          displayLabel: parentLabel ? `${parentLabel} > ${item.label}` : item.label
        });

        // Recursively add subItems
        if (item.subItems && item.subItems.length > 0) {
          flattenItems(item.subItems, item.label);
        }
      });
    };

    flattenItems([...mainNav, ...secondaryNav]);
    return items;
  }, [mainNav, secondaryNav]);

  // Filter items based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredItems([]);
      setIsSearchOpen(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = allMenuItems.filter(item =>
      item.label.toLowerCase().includes(query) ||
      item.parentLabel.toLowerCase().includes(query) ||
      item.href.toLowerCase().includes(query)
    ).slice(0, 8); // Limit to 8 results

    setFilteredItems(filtered);
    setIsSearchOpen(true);
    // NOTE: `allMenuItems` must NOT be a dependency here. It is rebuilt with a
    // fresh reference on every render (derived from the non-memoised dynamicNav),
    // so depending on it would re-run this effect every render and setState an
    // infinite loop ("Maximum update depth exceeded"). searchQuery is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Handle item selection
  const handleItemSelect = (item) => {
    navigate(item.href);
    clearQuickNavigateQuery();
  };

  const handleQuickNavigateKeyDown = (e) => {
    if (e.key === 'Escape') {
      clearQuickNavigateQuery();
    }
  };

  const handleQuickNavigateActivate = (event) => {
    event.stopPropagation();
    activateQuickNavigate();
    if (searchQuery.trim()) {
      setIsSearchOpen(true);
    }
    window.setTimeout(() => {
      quickNavigateInputRef.current?.focus({ preventScroll: true });
    }, 50);
  };

  return (
    <header className={cn(
      "sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      "transition-colors duration-200"
    )}>
      <div className="flex h-14 items-center justify-between px-4 sm:px-6">
        {/* Sidebar toggle (collapse / expand) — visible on all screen sizes */}
        <div className="flex items-center">
          <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
        </div>

        {/* Quick navigate + employee search — toggle expand, same width when open */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <div
            className={cn(
              "relative search-container shrink-0 flex items-center",
              HEADER_SEARCH_TRANSITION,
              isQuickExpanded ? HEADER_SEARCH_WIDTH : HEADER_SEARCH_COLLAPSED
            )}
          >
            <div className={headerSearchBarClass(isQuickExpanded)}>
              <button
                type="button"
                onClick={handleQuickNavigateActivate}
                className={headerSearchIconClass(isQuickExpanded, "left")}
                title="Quick navigate"
                aria-label="Quick navigate"
                aria-expanded={isQuickExpanded}
              >
                <Search className="h-4 w-4" />
              </button>

              {isQuickExpanded && (
                <Input
                  ref={quickNavigateInputRef}
                  type="search"
                  name="site-quick-navigate"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-form-type="other"
                  role="searchbox"
                  placeholder="Quick navigate..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleQuickNavigateKeyDown}
                  onFocus={() => {
                    activateQuickNavigate();
                    if (searchQuery.trim()) setIsSearchOpen(true);
                  }}
                  className="h-9 flex-1 min-w-0 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 pl-2 pr-2 placeholder:text-muted-foreground"
                />
              )}
            </div>

            {isQuickExpanded && isSearchOpen && filteredItems.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                <div className="p-2">
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                    Quick Navigation
                  </div>
                  {filteredItems.map((item, index) => (
                    <button
                      key={`${item.href}-${index}`}
                      onClick={() => handleItemSelect(item)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-md",
                        "hover:bg-accent hover:text-accent-foreground",
                        "transition-colors duration-150",
                        "flex items-center gap-2"
                      )}
                    >
                      {item.icon && (
                        <div className="flex-shrink-0 w-4 h-4 text-muted-foreground">
                          {React.createElement(item.icon, { className: "w-4 h-4" })}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {item.label}
                        </div>
                        {item.parentLabel && (
                          <div className="text-xs text-muted-foreground truncate">
                            {item.parentLabel}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-xs text-muted-foreground">
                        ↵
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isQuickExpanded && isSearchOpen && searchQuery && filteredItems.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50">
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No results found for "{searchQuery}"
                </div>
              </div>
            )}
          </div>

          <EmployeeSearchBar
            expanded={isEmployeeExpanded}
            onActivate={() => setActiveSearch('employee')}
          />
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-3">
          {/* Branch indicator (org admin). Hidden on Settings in All Branches —
              Configuration shows its own branch picker only in that mode. */}
          {showHeaderBranchControl && (
            <BranchHeaderControl />
          )}
          {
            location.pathname !== '/calendar' && location.pathname !== '/home' && (
              <button
                onClick={() => navigate("/calendar")}
                className="p-2.5 rounded-full hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
              >
                <CalendarDays className="h-4 w-4" />
              </button>
            )
          }
          <NotificationDropdown />
          <UserDropdown />
        </div>
      </div>
    </header>
  );
}